import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { ACCOUNT_UNVERIFIED_ERROR, fetchAccessToken, fetchUser, loginUser, registerUser, logoutUser } from '../api/auth';
import type { User } from '../types/auth';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  accessToken: string | null;
  /** Current province ID for the selected world (convenience getter) */
  provinceId: string | null;
  /** Current world ID */
  worldId: string | null;
  login: (email: string, password: string, rememberMe: boolean) => Promise<string | null>;
  logout: () => Promise<void>;
  register: (email: string, password: string) => Promise<string | null>;
  refreshUser: () => Promise<void>;
  /** Select a different world (for multi-world users) */
  selectWorld: (worldId: string) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

/** Gets the first world ID from provincesByWorld, or null if empty */
function getDefaultWorldId(provincesByWorld: Record<string, string>): string | null {
  const worldIds = Object.keys(provincesByWorld);
  return worldIds.length > 0 ? worldIds[0] : null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Token refresh callback for API client - returns new token or null
  const refreshTokenCallback = useCallback(async (): Promise<string | null> => {
    const { data: tokenData } = await fetchAccessToken();
    if (tokenData) {
      setAccessToken(tokenData.accessToken);
      api.setAccessToken(tokenData.accessToken);
      return tokenData.accessToken;
    }
    // Refresh failed - session expired, clear auth state
    // ProtectedRoute will detect !isAuthenticated, save returnUrl, redirect to /login
    setAccessToken(null);
    api.setAccessToken(null);
    setUser(null);
    return null;
  }, []);

  const initializeAuth = useCallback(async () => {
    const { data: tokenData } = await fetchAccessToken();
    if (tokenData) {
      setAccessToken(tokenData.accessToken);
      api.setAccessToken(tokenData.accessToken);
      const userData = await fetchUser(tokenData.accessToken);
      if (userData) {
        // Merge provincesByWorld from token response (authoritative source)
        const currentWorldId = getDefaultWorldId(tokenData.provincesByWorld);
        setUser({
          ...userData,
          provincesByWorld: tokenData.provincesByWorld,
          currentWorldId,
        });
      }
    }
  }, []);

  // Register refresh callback with API client
  useEffect(() => {
    api.setRefreshCallback(refreshTokenCallback);
    return () => api.setRefreshCallback(null);
  }, [refreshTokenCallback]);

  // Initialize auth on mount
  useEffect(() => {
    async function initAuth() {
      setIsLoading(true);
      await initializeAuth();
      setIsLoading(false);
    }

    initAuth();
  }, [initializeAuth]);

  const refreshUser = useCallback(async () => {
    // Refresh access token first (it may contain updated provincesByWorld after province creation)
    console.log('[AuthContext] refreshUser called');
    const { data: tokenData } = await fetchAccessToken();
    console.log('[AuthContext] tokenData:', tokenData);
    if (tokenData) {
      setAccessToken(tokenData.accessToken);
      api.setAccessToken(tokenData.accessToken);

      // Then fetch updated user data
      const userData = await fetchUser(tokenData.accessToken);
      console.log('[AuthContext] userData:', userData);
      if (userData) {
        // Merge provincesByWorld from token response (authoritative source)
        // Keep current world selection if it's still valid, otherwise default
        const currentWorldId = user?.currentWorldId && tokenData.provincesByWorld[user.currentWorldId]
          ? user.currentWorldId
          : getDefaultWorldId(tokenData.provincesByWorld);
        const newUser = {
          ...userData,
          provincesByWorld: tokenData.provincesByWorld,
          currentWorldId,
        };
        console.log('[AuthContext] Setting user with provincesByWorld:', newUser.provincesByWorld);
        setUser(newUser);
      }
    }
  }, [user?.currentWorldId]);

  const login = async (
    email: string,
    password: string,
    rememberMe: boolean
  ): Promise<string | null> => {
    const { error } = await loginUser(email, password, rememberMe);

    if (error) {
      return error;
    }

    // Get access token
    const { data: tokenData, error: tokenError } = await fetchAccessToken();
    if (!tokenData) {
      if (tokenError === ACCOUNT_UNVERIFIED_ERROR) {
        return ACCOUNT_UNVERIFIED_ERROR;
      }
      return 'Failed to get access token';
    }

    setAccessToken(tokenData.accessToken);
    api.setAccessToken(tokenData.accessToken);

    // Get user info
    const userData = await fetchUser(tokenData.accessToken);
    if (!userData) {
      return 'Failed to get user info';
    }

    // Merge provincesByWorld from token response
    const currentWorldId = getDefaultWorldId(tokenData.provincesByWorld);
    setUser({
      ...userData,
      provincesByWorld: tokenData.provincesByWorld,
      currentWorldId,
    });

    // Redirect based on whether user has a province
    const returnUrl = sessionStorage.getItem('returnUrl');
    sessionStorage.removeItem('returnUrl');

    const hasProvince = Object.keys(tokenData.provincesByWorld).length > 0;
    if (returnUrl) {
      navigate(returnUrl);
    } else if (hasProvince) {
      navigate('/province');
    } else {
      navigate('/create-province');
    }

    return null;
  };

  const logout = async (): Promise<void> => {
    await logoutUser();

    setAccessToken(null);
    api.setAccessToken(null);
    setUser(null);
    navigate('/');
  };

  const register = async (
    email: string,
    password: string
  ): Promise<string | null> => {
    const { error } = await registerUser(email, password);
    return error;
  };

  const selectWorld = useCallback((worldId: string) => {
    if (user && user.provincesByWorld[worldId]) {
      setUser({ ...user, currentWorldId: worldId });
    }
  }, [user]);

  // Computed values
  const worldId = user?.currentWorldId ?? null;
  const provinceId = worldId && user ? user.provincesByWorld[worldId] ?? null : null;

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        accessToken,
        provinceId,
        worldId,
        login,
        logout,
        register,
        refreshUser,
        selectWorld,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
